import { OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type GeoLocation } from "@src/appV2/Location";
import {
  type ClaimShiftRequest,
  useClaimShift,
} from "@src/appV2/OpenShifts/ShiftAction/api/useClaimShift";
import { useShiftDiscoverySearchMode } from "@src/appV2/redesign/ShiftDiscovery/useShiftDiscoverySearchMode";
import { useCallback, useState } from "react";

import { APP_V2_APP_EVENTS, logError } from "../../../../lib/analytics";
import { isSingleOrientationShift } from "../utils";

const MISSING_OFFER_ERROR_MESSAGE = "No offer found for shift";

interface FailedShift {
  shiftId: string;
  error: unknown;
}

interface BookBundleShiftsResult {
  bookedShiftIds: string[];
  failedShifts: FailedShift[];
}

export function useBookBundleShifts(bundleShifts: OpenShift[], offersIdMap: Map<string, string>) {
  const { mutateAsync: claimShift } = useClaimShift();
  const searchMode = useShiftDiscoverySearchMode();
  const [isBooking, setIsBooking] = useState(false);

  const bookBundleShifts = useCallback(
    async (
      selectedShiftIds: Set<string>,
      geoLocation?: GeoLocation
    ): Promise<BookBundleShiftsResult> => {
      setIsBooking(true);

      try {
        const shiftsToBook = bundleShifts.filter((shift) => selectedShiftIds.has(shift.id));
        const orientationShift = shiftsToBook.find((shift) => isSingleOrientationShift(shift));
        const nonOrientationShifts = isDefined(orientationShift)
          ? shiftsToBook.filter((shift) => shift.id !== orientationShift.id)
          : shiftsToBook;

        const failedShifts: FailedShift[] = [];
        const bookedShiftIds: string[] = [];

        const buildClaimRequest = (shift: OpenShift): ClaimShiftRequest | undefined => {
          const offerId = offersIdMap.get(shift.id);
          if (!isDefined(offerId)) {
            logError(APP_V2_APP_EVENTS.SHIFT_BUNDLE_NO_OFFER_FOUND_FAILURE, {
              error: new Error(MISSING_OFFER_ERROR_MESSAGE),
              metadata: { shiftId: shift.id, offerId },
            });
            failedShifts.push({ shiftId: shift.id, error: MISSING_OFFER_ERROR_MESSAGE });
            return undefined;
          }
          return {
            shiftId: shift.id,
            offerId,
            searchMode,
            latitude: geoLocation?.latitude,
            longitude: geoLocation?.longitude,
          };
        };

        // Returns true if non-orientation shifts should still be attempted.
        // On any failure path, marks every non-orientation shift as skipped and returns false
        const tryBookOrientationShiftFirst = async (shift: OpenShift): Promise<boolean> => {
          const request = buildClaimRequest(shift);
          if (!isDefined(request)) {
            markNonOrientationShiftsSkipped();
            return false;
          }
          try {
            await claimShift(request);
            bookedShiftIds.push(shift.id);
            return true;
          } catch (error) {
            failedShifts.push({ shiftId: shift.id, error });
            markNonOrientationShiftsSkipped();
            return false;
          }
        };

        function markNonOrientationShiftsSkipped() {
          for (const shift of nonOrientationShifts) {
            failedShifts.push({
              shiftId: shift.id,
              error: "Skipped: orientation shift booking failed",
            });
          }
        }

        if (isDefined(orientationShift)) {
          const didBookOrientation = await tryBookOrientationShiftFirst(orientationShift);
          if (!didBookOrientation) {
            return { bookedShiftIds, failedShifts };
          }
        }

        const claimRequests: ClaimShiftRequest[] = [];
        for (const shift of nonOrientationShifts) {
          const request = buildClaimRequest(shift);
          if (isDefined(request)) {
            claimRequests.push(request);
          }
        }

        const results = await Promise.allSettled(
          claimRequests.map(async (request) => {
            await claimShift(request);
            return request.shiftId;
          })
        );

        for (const [index, result] of results.entries()) {
          if (result.status === "fulfilled") {
            bookedShiftIds.push(result.value);
          } else {
            failedShifts.push({
              shiftId: claimRequests[index].shiftId,
              error: result.reason,
            });
          }
        }

        return { bookedShiftIds, failedShifts };
      } finally {
        setIsBooking(false);
      }
    },
    [bundleShifts, claimShift, searchMode, offersIdMap]
  );

  return {
    bookBundleShifts,
    isBooking,
  };
}
